import { 
  Zap, 
  CheckCircle, 
  FileCheck, 
  Calculator,
  BookOpen,
  Code,
  Users,
  TrendingUp,
  Brain,
  Building,
  Target
} from "lucide-react";

export const mockData = {
  howItWorksSteps: [
    {
      icon: Target,
      title: "Data configuration",
      description: "We share the exact parameters to pull reports from, whether that's Toast, Clover or ADP."
    },
    {
      icon: Brain,
      title: "Data Analysis",
      description: "We extract, analyze and verify your data with robust financial modeling for FICA tip credits."
    },
    {
      icon: TrendingUp,
      title: "Custom models",
      description: "All our calculations, estimated credit and financial models are bespoke to your business."
    },
    {
      icon: FileCheck,
      title: "Audit trail",
      description: "We generate comprehensive audit documentation both for your CPA to review and IRS audits."
    }
  ],
  
  // New categorized blog structure
  blogCategories: [
    {
      id: "tax-recovery",
      title: "Tax Recovery Insights",
      description: "FICA tip credits, compliance, and recovery strategies",
      icon: Calculator,
      color: "blue",
      posts: [
        {
          id: "fica-explained",
          title: "The FICA Tip Credit, Explained in 3 Minutes",
          excerpt: "Understanding the basics of FICA tip credits and how they can save your business money.",
          readTime: 3,
          author: "Jonathan Michael",
          date: "2025-09-06",
          tags: ["FICA", "Tax Credits", "Basics"],
          componentIndex: 0
        },
        {
          id: "fica-tip-credit-makeover",
          title: "The FICA Tip Credit Gets a Makeover: Now Available for Salons and Beauty Businesses",
          excerpt: "Congress expanded the FICA Tip Credit beyond restaurants to include beauty service businesses. Starting January 1, 2025, salons can claim the same tax relief.",
          readTime: 6,
          author: "Jonathan Michael",
          date: "2025-09-05",
          tags: ["FICA", "Beauty Salons", "Tax Credits", "2025 Law Changes"],
          componentIndex: 8
        },
        {
          id: "restaurant-money-vault",
          title: "Why Your Restaurant's Money Is Sitting in the IRS Vault (And How to Get It Back)",
          excerpt: "Every time you pay taxes on tips, you're making an interest-free loan to the IRS. Here's how to get that money back.",
          readTime: 4,
          author: "Jonathan Michael", 
          date: "2025-09-04",
          tags: ["Tax Recovery", "Restaurant", "IRS"],
          componentIndex: 3
        },
        {
          id: "common-mistakes",
          title: "The 5 Most Common FICA Tip Credit Mistakes (And How to Avoid Them)",
          excerpt: "Small errors can trigger IRS scrutiny or disallow legitimate credits. Here are the most common mistakes and how to avoid them.",
          readTime: 5,
          author: "Jonathan Michael",
          date: "2025-09-03",
          tags: ["Compliance", "Mistakes", "IRS"],
          componentIndex: 5
        }
      ]
    },
    {
      id: "ai-finance-accounting",
      title: "AI in Finance & Accounting",
      description: "AI research, automation, and workflow transformation for financial services",
      icon: Brain,
      color: "purple",
      posts: [
        {
          id: "pos-payroll-irs",
          title: "POS → Payroll → IRS: A Cleaner Data Path for Credits",
          excerpt: "How modern integrations make tip credit recovery seamless and error-free.",
          readTime: 4,
          author: "Jonathan Michael",
          date: "2025-09-02",
          tags: ["Integration", "Data", "Automation"],
          componentIndex: 1
        },
        {
          id: "ai-accounting-revolution",
          title: "Why AI-Powered Accounting Is Finally Here (And Why FICA Credits Are Leading the Way)",
          excerpt: "As Andreessen Horowitz predicted, AI is transforming accounting workflows. Here's how specialized AI solutions like Credentra are solving real problems today.",
          readTime: 6,
          author: "Jonathan Michael",
          date: "2025-09-01",
          tags: ["AI", "Accounting Revolution", "FICA"],
          componentIndex: 6
        },
        {
          id: "buried-tax-code-1993",
          title: "Buried in the Tax Code Since 1993: The Credit 85% of Business Owners Ignore",
          excerpt: "Since 1993, a tax credit worth up to $30,000+ annually has been sitting in the IRS code, waiting for restaurant owners to claim it. Only 15% ever do. This July, beauty salons just became eligible.",
          readTime: 7,
          author: "Credentra Team",
          date: "2025-09-01",
          tags: ["FICA", "Tax Code", "Business Credits", "Beauty Salons"],
          componentIndex: 7
        }
      ]
    },
    {
      id: "cpa-resources",
      title: "CPA Resources", 
      description: "Professional resources for accounting firms",
      icon: Users,
      color: "green",
      posts: [
        {
          id: "cpa-workpapers",
          title: "What CPAs Need from Tip-Credit Workpapers (and How to Deliver It Fast)",
          excerpt: "Essential documentation requirements and how to streamline the process.",
          readTime: 6,
          author: "Jonathan Michael",
          date: "2025-08-31",
          tags: ["CPA", "Documentation", "Workflow"],
          componentIndex: 2
        },
        {
          id: "cpa-secret-weapon",
          title: "The CPA's Secret Weapon: How to Turn Restaurant Clients Into Your Most Profitable Accounts",
          excerpt: "How smart CPAs are using tip credit recovery to deliver massive value and build their most profitable engagements.",
          readTime: 5,
          author: "Jonathan Michael",
          date: "2025-08-30",
          tags: ["CPA", "Business Growth", "Profitability"],
          componentIndex: 4
        }
      ]
    }
  ],
  
  // Legacy blog posts for backward compatibility
  blogPosts: [
    {
      title: "The FICA Tip Credit, Explained in 3 Minutes",
      excerpt: "Understanding the basics of FICA tip credits and how they can save your business money."
    },
    {
      title: "POS → Payroll → IRS: A Cleaner Data Path for Credits",
      excerpt: "How modern integrations make tip credit recovery seamless and error-free."
    },
    {
      title: "What CPAs Need from Tip-Credit Workpapers (and How to Deliver It Fast)",
      excerpt: "Essential documentation requirements and how to streamline the process."
    },
    {
      title: "Why Your Restaurant's Money Is Sitting in the IRS Vault (And How to Get It Back)",
      excerpt: "Every time you pay taxes on tips, you're making an interest-free loan to the IRS. Here's how to get that money back."
    },
    {
      title: "The CPA's Secret Weapon: How to Turn Restaurant Clients Into Your Most Profitable Accounts",
      excerpt: "How smart CPAs are using tip credit recovery to deliver massive value and build their most profitable engagements."
    },
    {
      title: "The 5 Most Common FICA Tip Credit Mistakes (And How to Avoid Them)",
      excerpt: "Small errors can trigger IRS scrutiny or disallow legitimate credits. Here are the most common mistakes and how to avoid them."
    }
  ],
  
  faqItems: [
    {
      question: "What is the FICA Tip Credit?",
      answer: "The FICA Tip Credit is a permanent federal tax credit that allows businesses where tipping is customary—such as restaurants, bars, hotels, and cafes—to reduce their income tax liability by claiming a credit on the employer's share of Social Security and Medicare (FICA) taxes paid on employee tips."
    },
    {
      question: "Who is eligible to claim the FICA Tip Credit?",
      answer: "You may qualify if:\n\n• Your employees receive tips directly from customers.\n• Tips are reported to you through payroll.\n• You already pay the employer portion of FICA taxes (7.65%) on those tips.\n\nIndustries include:\n• Restaurants and bars\n• Coffee shops and cafes\n• Hotels and catering companies\n• Other hospitality businesses where tipping is customary"
    },
    {
      question: "How much is the credit worth?",
      answer: "The credit is equal to the employer portion (7.65%) of FICA taxes paid on reported tips exceeding the federal minimum wage requirement. For example, if an employee earns $15,000 in reported tips, the business can potentially claim a credit of about $1,147.50."
    },
    {
      question: "Does the FICA Tip Credit affect employees' wages or tips?",
      answer: "No. Employees keep their full wages and tips. The credit only reduces the employer's federal income tax liability."
    },
    {
      question: "How do I claim the credit?",
      answer: "• Complete IRS Form 8846 (Credit for Employer Social Security and Medicare Taxes Paid on Certain Employee Tips).\n• Attach it to your business's annual income tax return.\n• Maintain accurate payroll and tip reporting records."
    },
    {
      question: "What records do I need to keep?",
      answer: "You must keep:\n• Employee payroll records\n• Reported tips (via POS or payroll system)\n• Proof of FICA tax payments\n\nAccurate, auditable data is critical if the IRS reviews your claim."
    },
    {
      question: "Is this credit refundable?",
      answer: "No. The FICA Tip Credit is a nonrefundable tax credit. It can reduce your federal income tax liability to zero but won't generate a cash refund beyond your tax liability."
    },
    {
      question: "Can I still claim the credit if I use a payroll provider?",
      answer: "Yes. Even if your payroll provider calculates and pays FICA taxes, you can still claim the credit as long as tip data is properly tracked and reported."
    },
    {
      question: "How far back can I claim the credit?",
      answer: "Generally, you can file an amended return to claim the credit for the past three years (the standard IRS statute of limitations)."
    },
    {
      question: "Is your software platform ready?",
      answer: "Our automated AI platform is currently in development. We're actively serving clients through partnerships with qualified accounting firms using robust, bespoke Excel models tailored to each business's specific payroll and tip structure. This ensures accurate FICA tip credit calculations while our software completes development. You'll receive the same high-quality service and results during this transition period."
    }
  ]
};